import { useState, useEffect, useCallback } from "react";
import {
  HiOutlineCalendarDays, HiOutlinePlus, HiOutlinePencil, HiOutlineTrash,
  HiOutlineMapPin, HiOutlineUserGroup, HiOutlineXMark, HiOutlineArrowLeft,
  HiOutlineMagnifyingGlass,
  HiOutlineClock, HiOutlineCheckCircle, HiOutlineHome, HiOutlineCog6Tooth,
} from "react-icons/hi2";
import AdminLayout from "./AdminLayout";
import { retreatBlocksAPI, roomTypesAPI } from "../services/api";
import { useCurrentAdmin, hasPermission } from "./permissions";
import PropertyRooms from "./PropertyRooms";
import toast from "react-hot-toast";
import "./AdminRetreatBlocks.css";

const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US", { year:"numeric", month:"short", day:"numeric" }) : "—";
const fmtMoney = (n) => `$${Number(n||0).toLocaleString("en-US",{minimumFractionDigits:0})}`;

const STATUS_META = {
  upcoming:    { label:"Upcoming",    color:"#1d4ed8", bg:"rgba(37,99,235,0.1)" },
  open:        { label:"Open",        color:"#166534", bg:"rgba(22,163,74,0.1)" },
  in_progress: { label:"In Progress", color:"#92400e", bg:"rgba(217,119,6,0.1)" },
  completed:   { label:"Completed",   color:"#4a433b", bg:"rgba(107,98,88,0.1)" },
  cancelled:   { label:"Cancelled",   color:"#b91c1c", bg:"rgba(220,38,38,0.1)" },
};

/* ── Room Types management panel ── */
function RoomTypesPanel({ onClose }) {
  const [types,   setTypes]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | type obj
  const [form,    setForm]    = useState({ name:"", rate:0, nightly_rate:0, is_free:false, description:"" });
  const [saving,  setSaving]  = useState(false);

  const fetchTypes = useCallback(() => {
    setLoading(true);
    roomTypesAPI.list(true)
      .then(res => setTypes(res?.data || []))
      .catch(() => toast.error("Failed to load room types."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchTypes(); }, [fetchTypes]);

  const openNew  = () => { setEditing("new"); setForm({ name:"", rate:0, nightly_rate:0, is_free:false, description:"" }); };
  const openEdit = (t) => { setEditing(t); setForm({ name:t.name, rate:t.rate, nightly_rate:t.nightly_rate||0, is_free:!!t.is_free, description:t.description||"" }); };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name is required."); return; }
    setSaving(true);
    try {
      if (editing === "new") { await roomTypesAPI.create(form); toast.success("Room type created."); }
      else { await roomTypesAPI.update(editing.id, form); toast.success("Room type updated."); }
      setEditing(null);
      fetchTypes();
    } catch (err) { toast.error(err.message || "Failed."); }
    finally { setSaving(false); }
  };

  const handleToggleActive = async (t) => {
    try { await roomTypesAPI.update(t.id, { active: !t.active }); fetchTypes(); }
    catch (err) { toast.error(err.message || "Failed."); }
  };

  const handleDelete = async (t) => {
    try {
      const res = await roomTypesAPI.remove(t.id);
      toast.success(res?.message || "Done.");
      fetchTypes();
    } catch (err) { toast.error(err.message || "Failed."); }
  };

  return (
    <div className="acard arb__form-panel">
      <div className="arb__form-head">
        <p className="arb__form-title">Room Types &amp; Rates</p>
        <button type="button" className="acr__icon-btn" onClick={onClose}><HiOutlineXMark/></button>
      </div>
      <div className="arb__form-body">
        <p style={{fontSize:"0.82rem",color:"#6b6258",margin:"0 0 1rem"}}>
          Define the room tiers you offer (e.g. Scholarship, Shared, Private) with their rates.
          You'll allocate how many of each are available per retreat block.
        </p>

        {editing ? (
          <div className="arb__roomtype-form">
            <div className="arb__form-row">
              <div className="arb__form-field">
                <label className="arb__form-label">Name</label>
                <input className="arb__form-input" value={form.name}
                  onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="e.g. Private Room"/>
              </div>
              <div className="arb__form-field">
                <label className="arb__form-label">Flat rate (retreats without pathways)</label>
                <input type="number" min="0" className="arb__form-input" value={form.rate}
                  disabled={form.is_free}
                  onChange={e=>setForm(p=>({...p,rate:parseFloat(e.target.value)||0}))}/>
              </div>
              <div className="arb__form-field">
                <label className="arb__form-label">Upcharge per night (pathways)</label>
                <input type="number" min="0" className="arb__form-input" value={form.nightly_rate}
                  disabled={form.is_free}
                  onChange={e=>setForm(p=>({...p,nightly_rate:parseFloat(e.target.value)||0}))}/>
              </div>
            </div>
            <label style={{display:"flex",alignItems:"center",gap:"0.5rem",fontSize:"0.82rem",cursor:"pointer"}}>
              <input type="checkbox" checked={form.is_free}
                onChange={e=>setForm(p=>({...p,is_free:e.target.checked, rate: e.target.checked?0:p.rate, nightly_rate: e.target.checked?0:p.nightly_rate}))}/>
              This is a free / scholarship tier
            </label>
            <div className="arb__form-field">
              <label className="arb__form-label">Description (optional)</label>
              <input className="arb__form-input" value={form.description}
                onChange={e=>setForm(p=>({...p,description:e.target.value}))}/>
            </div>
            <div style={{display:"flex",gap:"0.5rem",justifyContent:"flex-end"}}>
              <button type="button" className="a-btn a-btn--outline" onClick={()=>setEditing(null)}>Cancel</button>
              <button type="button" className="a-btn a-btn--rust" onClick={handleSave} disabled={saving}>
                {saving?"Saving…":editing==="new"?"Add Room Type":"Save Changes"}
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="a-btn a-btn--outline" onClick={openNew} style={{marginBottom:"1rem"}}>
            <HiOutlinePlus/> Add Room Type
          </button>
        )}

        {loading ? <div className="arb__loading"><div className="acr__spinner"/></div> : (
          <div className="arb__roomtype-list">
            {types.map(t => (
              <div key={t.id} className={`arb__roomtype-row ${!t.active?"arb__roomtype-row--inactive":""}`}>
                <div style={{width:"100%"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <div className="arb__roomtype-info">
                      <p className="arb__roomtype-name">{t.name} {!t.active && <span style={{color:"#9ca3af",fontWeight:400}}>(inactive)</span>}</p>
                      <p className="arb__roomtype-rate">{t.is_free ? "Free / Scholarship" : `${fmtMoney(t.rate)} per person${Number(t.nightly_rate) > 0 ? ` · +${fmtMoney(t.nightly_rate)}/night on pathways` : ""}`}</p>
                    </div>
                    <div style={{display:"flex",gap:"0.4rem"}}>
                      <button type="button" className="a-btn a-btn--ghost" onClick={()=>openEdit(t)}><HiOutlinePencil/></button>
                      <button type="button" className="a-btn a-btn--ghost" onClick={()=>handleToggleActive(t)}>
                        {t.active ? "Deactivate" : "Activate"}
                      </button>
                      <button type="button" className="a-btn a-btn--ghost" style={{color:"#dc2626"}} onClick={()=>handleDelete(t)}>
                        <HiOutlineTrash/>
                      </button>
                    </div>
                  </div>
                  <RoomTypePhotos roomType={t} onChanged={fetchTypes}/>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Photo gallery + uploader for one room type ── */
function RoomTypePhotos({ roomType, onChanged }) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      await roomTypesAPI.uploadPhotos(roomType.id, files);
      toast.success(`${files.length} photo${files.length!==1?"s":""} uploaded.`);
      onChanged();
    } catch (err) { toast.error(err.message || "Upload failed."); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handleDeletePhoto = async (photoId) => {
    try { await roomTypesAPI.removePhoto(roomType.id, photoId); onChanged(); }
    catch (err) { toast.error(err.message || "Failed."); }
  };

  const apiBase = (process.env.REACT_APP_API_URL || "http://localhost:5000/api").replace(/\/api$/, "");

  return (
    <div className="arb__photo-strip">
      {(roomType.photos || []).map(p => (
        <div key={p.id} className="arb__photo-thumb">
          <img src={`${apiBase}/${p.file_path}`} alt=""/>
          <button type="button" className="arb__photo-remove" onClick={()=>handleDeletePhoto(p.id)}>
            <HiOutlineXMark/>
          </button>
        </div>
      ))}
      <label className="arb__photo-add">
        <input type="file" accept="image/jpeg,image/png,image/webp" multiple
          style={{display:"none"}} onChange={handleUpload} disabled={uploading}/>
        {uploading ? "…" : "+ Photos"}
      </label>
    </div>
  );
}

/* ── Create / Edit block form with room type allocation ── */
function BlockForm({ block, onCancel, onSaved }) {
  const [form, setForm] = useState({
    name:       block?.name       || "",
    start_date: block?.start_date ? block.start_date.slice(0,10) : "",
    end_date:   block?.end_date   ? block.end_date.slice(0,10)   : "",
    location:   block?.location   || "",
    status:     block?.status     || "upcoming",
    notes:      block?.notes      || "",
  });
  const [allTypes,    setAllTypes]    = useState([]);
  const [allocations, setAllocations] = useState({}); // { room_type_id: capacity }
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");

  useEffect(() => {
    roomTypesAPI.list(true).then(res => {
      const types = res?.data || [];
      setAllTypes(types);
      const initial = {};
      types.forEach(t => {
        const existing = block?.room_types?.find(rt => rt.room_type_id === t.id);
        initial[t.id] = existing ? existing.capacity : 0;
      });
      setAllocations(initial);
    }).catch(() => toast.error("Failed to load room types."));
  }, [block]);

  const totalCapacity = Object.values(allocations).reduce((s,v) => s + (parseInt(v)||0), 0);

  const handleSave = async () => {
    setError("");
    if (!form.name.trim() || !form.start_date || !form.end_date) {
      setError("Name, start date, and end date are required."); return;
    }
    if (new Date(form.end_date) < new Date(form.start_date)) {
      setError("End date cannot be before start date."); return;
    }
    if (totalCapacity === 0) {
      setError("Please allocate capacity to at least one room type."); return;
    }
    setSaving(true);
    try {
      const room_types = Object.entries(allocations)
        .filter(([, cap]) => parseInt(cap) > 0)
        .map(([room_type_id, capacity]) => ({ room_type_id: parseInt(room_type_id), capacity: parseInt(capacity) }));

      if (block) {
        await retreatBlocksAPI.update(block.id, { ...form, room_types });
        toast.success("Retreat block updated.");
      } else {
        await retreatBlocksAPI.create({ ...form, room_types });
        toast.success("Retreat block created.");
      }
      onSaved();
    } catch (err) {
      setError(err.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="acard arb__form-panel">
      <div className="arb__form-head">
        <p className="arb__form-title">{block ? `Edit: ${block.name}` : "Create Retreat Block"}</p>
        <button type="button" className="acr__icon-btn" onClick={onCancel}><HiOutlineXMark/></button>
      </div>
      <div className="arb__form-body">
        <div className="arb__form-field">
          <label className="arb__form-label">Retreat Name</label>
          <input className="arb__form-input" placeholder="e.g. March 2026 Retreat"
            value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))}/>
        </div>
        <div className="arb__form-row">
          <div className="arb__form-field">
            <label className="arb__form-label">Start Date</label>
            <input type="date" className="arb__form-input"
              value={form.start_date} onChange={e=>setForm(p=>({...p,start_date:e.target.value}))}/>
          </div>
          <div className="arb__form-field">
            <label className="arb__form-label">End Date</label>
            <input type="date" className="arb__form-input"
              value={form.end_date} onChange={e=>setForm(p=>({...p,end_date:e.target.value}))}/>
          </div>
        </div>
        <div className="arb__form-row">
          <div className="arb__form-field">
            <label className="arb__form-label">Location</label>
            <input className="arb__form-input" placeholder="e.g. Cerro Escondido, Colombia"
              value={form.location} onChange={e=>setForm(p=>({...p,location:e.target.value}))}/>
          </div>
          <div className="arb__form-field">
            <label className="arb__form-label">Status</label>
            <select className="arb__form-input arb__form-select"
              value={form.status} onChange={e=>setForm(p=>({...p,status:e.target.value}))}>
              {Object.entries(STATUS_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        </div>

        {/* Room type capacity allocation — the core of the booking system */}
        <div className="arb__form-field">
          <label className="arb__form-label">Room Type Allocation</label>
          <p style={{fontSize:"0.78rem",color:"#6b6258",margin:"0 0 0.5rem"}}>
            How many spots of each room type are available for this specific retreat?
          </p>
          {allTypes.length === 0 ? (
            <p style={{fontSize:"0.82rem",color:"#9ca3af"}}>No room types defined yet — add some first.</p>
          ) : (
            <div className="arb__alloc-grid">
              {allTypes.filter(t => t.active || allocations[t.id] > 0).map(t => (
                <div key={t.id} className="arb__alloc-row">
                  <div className="arb__alloc-info">
                    <p className="arb__alloc-name">{t.name}</p>
                    <p className="arb__alloc-rate">{t.is_free ? "Free" : fmtMoney(t.rate)}</p>
                  </div>
                  <input type="number" min="0" className="arb__form-input arb__alloc-input"
                    value={allocations[t.id] ?? 0}
                    onChange={e => setAllocations(p => ({ ...p, [t.id]: parseInt(e.target.value)||0 }))}/>
                </div>
              ))}
            </div>
          )}
          <p style={{fontSize:"0.82rem",fontWeight:700,color:"#1b1b1b",marginTop:"0.65rem"}}>
            Total capacity: {totalCapacity}
          </p>
        </div>

        <div className="arb__form-field">
          <label className="arb__form-label">Notes (internal only)</label>
          <textarea className="arb__form-input arb__form-textarea" rows={3}
            value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}/>
        </div>
        {error && <p className="arb__form-error">{error}</p>}
      </div>
      <div className="arb__form-footer">
        <button type="button" className="a-btn a-btn--outline" onClick={onCancel}>Cancel</button>
        <button type="button" className="a-btn a-btn--rust" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : block ? "Save Changes" : "Create Block"}
        </button>
      </div>
    </div>
  );
}

/* ── Block detail view (room type breakdown + applicants) ── */
function BlockDetail({ blockId, onBack, onEdit, admin, canEdit }) {
  const [block,   setBlock]   = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDetail = useCallback(() => {
    setLoading(true);
    retreatBlocksAPI.getOne(blockId)
      .then(res => setBlock(res?.data || null))
      .catch(() => toast.error("Failed to load block."))
      .finally(() => setLoading(false));
  }, [blockId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  if (loading) return <div className="arb__loading"><div className="acr__spinner"/></div>;
  if (!block) return null;

  const meta = STATUS_META[block.status] || STATUS_META.upcoming;

  return (
    <div className="arb__detail">
      <button type="button" className="arb__back-btn" onClick={onBack}>
        <HiOutlineArrowLeft/> All Retreat Blocks
      </button>

      <div className="acard arb__detail-header">
        <div className="arb__detail-top">
          <div>
            <span className="arb__status-pill" style={{background:meta.bg,color:meta.color}}>{meta.label}</span>
            {block.isPastDue && (
              <span className="arb__pastdue-pill" title="This retreat's dates have passed but it hasn't been marked completed yet">
                <HiOutlineClock/> Past Due — Close It Out
              </span>
            )}
            <h2 className="arb__detail-title">{block.name}</h2>
            <p className="arb__detail-dates"><HiOutlineCalendarDays/> {fmt(block.start_date)} – {fmt(block.end_date)}</p>
            {block.location && <p className="arb__detail-location"><HiOutlineMapPin/> {block.location}</p>}
          </div>
          <button type="button" className="a-btn a-btn--outline" onClick={() => onEdit(block)}>
            <HiOutlinePencil/> Edit Block
          </button>
        </div>

        {/* Room type breakdown */}
        <div className="arb__roomtype-breakdown">
          {block.room_types.map(rt => {
            const used = rt.assigned_count + rt.offered_count;
            const pct = rt.capacity ? Math.min(100, Math.round((used / rt.capacity) * 100)) : 0;
            return (
              <div key={rt.room_type_id} className="arb__capacity-block">
                <div className="arb__capacity-row">
                  <span className="arb__capacity-label">
                    <HiOutlineHome/> {rt.name} {rt.is_free ? "(Free)" : `(${fmtMoney(rt.rate)})`}
                  </span>
                  <span className="arb__capacity-count">
                    {rt.assigned_count} confirmed{rt.offered_count > 0 ? ` · ${rt.offered_count} pending` : ""} / {rt.capacity}
                  </span>
                </div>
                <div className="arb__capacity-track">
                  <div className="arb__capacity-fill" style={{width:`${pct}%`, background: pct>=100?"#dc2626":pct>=75?"#d97706":"#16a34a"}}/>
                </div>
              </div>
            );
          })}
          <p style={{fontSize:"0.82rem",fontWeight:700,color:"#1b1b1b",marginTop:"0.5rem"}}>
            Total: {block.assigned_count} confirmed{block.offered_count>0?` · ${block.offered_count} pending`:""} / {block.total_capacity}
          </p>
        </div>

        {block.notes && <p className="arb__detail-notes">{block.notes}</p>}
      </div>

      {admin?.features?.propertyRooms && <PropertyRooms block={block} canEdit={canEdit} />}

      <div className="acard arb__applicants-card">
        <p className="p-section-title" style={{padding:"1.1rem 1.25rem 0"}}>Applicants</p>
        {block.applicants.length === 0 ? (
          <p className="aap__placeholder" style={{padding:"2rem",textAlign:"center"}}>No one assigned to this block yet.</p>
        ) : (
          <table className="aap__table" style={{width:"100%"}}>
            <thead>
              <tr><th>Name</th><th>Category</th><th>Room Type</th><th>Status</th><th>Contact</th></tr>
            </thead>
            <tbody>
              {block.applicants.map(a => (
                <tr key={a.id}>
                  <td style={{fontWeight:600}}>{a.first_name} {a.last_name}</td>
                  <td>{a.category}</td>
                  <td>{a.room_type_name || "—"}</td>
                  <td>
                    {a.queue_status === "assigned" ? (
                      <span style={{color:"#16a34a",fontWeight:700,fontSize:"0.8rem",display:"flex",alignItems:"center",gap:"0.3rem"}}>
                        <HiOutlineCheckCircle/> Confirmed
                      </span>
                    ) : (
                      <span style={{color:"#d97706",fontWeight:700,fontSize:"0.8rem",display:"flex",alignItems:"center",gap:"0.3rem"}}>
                        <HiOutlineClock/> Offer Pending
                      </span>
                    )}
                  </td>
                  <td style={{fontSize:"0.82rem",color:"#6b6258"}}>{a.email}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
}

/* ── Main page ── */
export default function AdminRetreatBlocks() {
  const { admin, loading: permLoading } = useCurrentAdmin();
  const canEdit = permLoading ? true : hasPermission(admin, "blocks.edit");

  const [blocks,   setBlocks]   = useState([]);
  const [counts,   setCounts]   = useState({ active: 0, past: 0, all: 0 });
  const [loading,  setLoading]  = useState(true);
  const [editing,  setEditing]  = useState(null); // null | "new" | block obj
  const [viewing,  setViewing]  = useState(null); // block id
  const [showRoomTypes, setShowRoomTypes] = useState(false);

  // Filtering happens server-side — with dozens of live retreats and many more
  // archived, loading everything and hiding it client-side doesn't scale.
  const [scope,  setScope]  = useState("active");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce so typing doesn't fire a request per keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchBlocks = useCallback(() => {
    setLoading(true);
    retreatBlocksAPI.list({ scope, search: debouncedSearch })
      .then(res => {
        setBlocks(res?.data || []);
        if (res?.counts) setCounts(res.counts);
      })
      .catch(() => toast.error("Failed to load retreat blocks."))
      .finally(() => setLoading(false));
  }, [scope, debouncedSearch]);

  useEffect(() => { fetchBlocks(); }, [fetchBlocks]);

  const handleDelete = (block) => {
    toast((t) => (
      <div style={{display:"flex",flexDirection:"column",gap:"0.5rem"}}>
        <p style={{margin:0,fontWeight:700}}>Delete "{block.name}"?</p>
        <p style={{margin:0,fontSize:"0.78rem",color:"#6b6258"}}>This cannot be undone.</p>
        <div style={{display:"flex",gap:"0.5rem"}}>
          <button onClick={async ()=>{
            toast.dismiss(t.id);
            try { await retreatBlocksAPI.remove(block.id); toast.success("Block deleted."); fetchBlocks(); }
            catch(err){ toast.error(err.message||"Failed."); }
          }} style={{background:"#dc2626",color:"#fff",border:"none",borderRadius:"5px",padding:"0.35rem 0.75rem",cursor:"pointer",fontWeight:700,fontSize:"0.78rem"}}>Delete</button>
          <button onClick={()=>toast.dismiss(t.id)} style={{background:"#f3f4f0",border:"none",borderRadius:"5px",padding:"0.35rem 0.75rem",cursor:"pointer",fontSize:"0.78rem"}}>Cancel</button>
        </div>
      </div>
    ), { duration:Infinity, style:{maxWidth:"300px"} });
  };

  if (viewing) {
    return (
      <AdminLayout pageTitle="Retreat Blocks" pageSubtitle="" permission="blocks.view">
        <BlockDetail blockId={viewing} admin={admin} canEdit={canEdit} onBack={()=>{ setViewing(null); fetchBlocks(); }}
          onEdit={(block)=>{ setViewing(null); setEditing(block); }}/>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTitle="Retreat Blocks" pageSubtitle="Schedule retreats and manage room capacity." permission="blocks.view">
      {canEdit && !editing && !showRoomTypes && (
        <div style={{display:"flex",justifyContent:"flex-end",gap:"0.5rem",marginBottom:"1rem"}}>
          <button type="button" className="a-btn a-btn--outline" onClick={()=>setShowRoomTypes(true)}>
            <HiOutlineCog6Tooth/> Room Types &amp; Rates
          </button>
          <button type="button" className="a-btn a-btn--rust" onClick={()=>setEditing("new")}>
            <HiOutlinePlus/> New Retreat Block
          </button>
        </div>
      )}

      {/* Scope tabs + search — keeps the list usable as retreat count grows */}
      {!editing && !showRoomTypes && (
        <div className="arb__filterbar">
          <div className="arb__scope-tabs">
            {[
              { key: "active", label: "Active",   count: counts.active },
              { key: "past",   label: "Past",     count: counts.past },
              { key: "all",    label: "All",      count: counts.all },
            ].map(t => (
              <button key={t.key} type="button"
                className={`arb__scope-tab ${scope === t.key ? "arb__scope-tab--active" : ""}`}
                onClick={() => setScope(t.key)}>
                {t.label}
                <span className="arb__scope-count">{t.count}</span>
              </button>
            ))}
          </div>
          <div className="arb__search-wrap">
            <HiOutlineMagnifyingGlass className="arb__search-icon"/>
            <input type="text" className="arb__search" placeholder="Search by name or location…"
              value={search} onChange={e => setSearch(e.target.value)}/>
            {search && (
              <button type="button" className="arb__search-clear" onClick={() => setSearch("")}>
                <HiOutlineXMark/>
              </button>
            )}
          </div>
        </div>
      )}

      {showRoomTypes && <RoomTypesPanel onClose={()=>{ setShowRoomTypes(false); fetchBlocks(); }}/>}

      {editing && (
        <BlockForm block={editing==="new"?null:editing}
          onCancel={()=>setEditing(null)}
          onSaved={()=>{ setEditing(null); fetchBlocks(); }}/>
      )}

      {!editing && !showRoomTypes && (
        loading ? <div className="arb__loading"><div className="acr__spinner"/></div>
        : blocks.length === 0 ? (
          <div className="acard arb__empty">
            <HiOutlineCalendarDays className="arb__empty-icon"/>
            {debouncedSearch || scope !== "active" || counts.all > 0 ? (
              <>
                <p className="arb__empty-title">Nothing matches</p>
                <p className="arb__empty-sub">
                  {debouncedSearch
                    ? `No retreats match "${debouncedSearch}" in this view.`
                    : "No retreats in this view."}
                  {" "}Try a different tab or clear your search.
                </p>
                {(debouncedSearch || scope !== "active") && (
                  <button type="button" className="a-btn a-btn--outline"
                    onClick={() => { setSearch(""); setScope("active"); }}>
                    Reset Filters
                  </button>
                )}
              </>
            ) : (
              <>
                <p className="arb__empty-title">No retreat blocks yet</p>
                <p className="arb__empty-sub">Create your first retreat block to start assigning applicants from the queue.</p>
              </>
            )}
          </div>
        ) : (
          <div className="arb__grid">
            {blocks.map(block => {
              const totalCap = block.room_types.reduce((s,rt)=>s+rt.capacity,0);
              const totalUsed = block.assigned_count;
              const pct = totalCap ? Math.min(100, Math.round((totalUsed / totalCap) * 100)) : 0;
              const meta = STATUS_META[block.status] || STATUS_META.upcoming;
              return (
                <div key={block.id} className="acard arb__card" onClick={()=>setViewing(block.id)}>
                  <div className="arb__card-top">
                    <div style={{display:"flex",alignItems:"center",gap:"0.4rem",flexWrap:"wrap"}}>
                      <span className="arb__status-pill" style={{background:meta.bg,color:meta.color}}>{meta.label}</span>
                      {block.isPastDue && (
                        <span className="arb__pastdue-pill" title="Dates have passed — mark this retreat completed">
                          <HiOutlineClock/> Past Due
                        </span>
                      )}
                    </div>
                    {canEdit && (
                      <button type="button" className="arb__card-delete" aria-label={`Delete or archive ${block.name}`}
                        onClick={(e)=>{e.stopPropagation();handleDelete(block);}}>
                        <HiOutlineTrash/>
                      </button>
                    )}
                  </div>
                  <p className="arb__card-title">{block.name}</p>
                  <p className="arb__card-dates"><HiOutlineCalendarDays/> {fmt(block.start_date)} – {fmt(block.end_date)}</p>
                  {block.location && <p className="arb__card-location"><HiOutlineMapPin/> {block.location}</p>}
                  <div className="arb__card-roomtypes">
                    {block.room_types.map(rt => (
                      <span key={rt.room_type_id} className="arb__roomtype-chip">
                        {rt.name}: {rt.assigned_count}/{rt.capacity}
                      </span>
                    ))}
                  </div>
                  {(block.cohort_count > 0 || block.unplaced_count > 0) && (
                    <div className="arb__card-cohorts">
                      {block.cohort_count > 0 && (
                        <span className="arb__cohort-chip">
                          <HiOutlineUserGroup/> {block.cohort_count} cohort{block.cohort_count !== 1 ? "s" : ""}
                        </span>
                      )}
                      {block.unplaced_count > 0 && (
                        <span className="arb__cohort-chip arb__cohort-chip--alert"
                          title="Confirmed attendees not yet placed in a ceremony cohort">
                          {block.unplaced_count} unplaced
                        </span>
                      )}
                    </div>
                  )}
                  <div className="arb__card-capacity">
                    <div className="arb__capacity-row">
                      <span className="arb__capacity-label"><HiOutlineUserGroup/> Total Capacity</span>
                      <span className="arb__capacity-count">{totalUsed} / {totalCap}</span>
                    </div>
                    <div className="arb__capacity-track">
                      <div className="arb__capacity-fill" style={{width:`${pct}%`, background: pct>=100?"#dc2626":pct>=75?"#d97706":"#16a34a"}}/>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </AdminLayout>
  );
}
